import React, { memo, useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { AppContext } from '@prisjakt/ui-app-context';
import Panel from '@prisjakt/ui-panel';
import PanelHeader from '@prisjakt/ui-panel-header';
import ProductImage from '@prisjakt/ui-product-image';
import ProductCard from '@prisjakt/ui-product-card';
import ExpandableGrid from '@prisjakt/ui-expandable-grid';
import MatchMedia from '../../../../components/match-media';

import { productNavigation } from '../../../product/actions/product';
import {
  POPULAR_CLICK,
  RECOMMENDED_CLICK,
} from '../../../../globals/helpers/navigation-action';
import { filterCategories } from './filter-categories';

import { InternalLink } from './styles';

const ProductRecommendations = props => {
  const { t } = useContext(AppContext);
  const { products } = props;

  if (!products) {
    return null;
  }

  const productTagManagerAction = products.fallbackToDefault
    ? POPULAR_CLICK
    : RECOMMENDED_CLICK;

  let items = products.nodes;

  // Respect removed interest if the recommendations are based on
  // browsing history and not popularity.
  if (!products.fallbackToDefault) {
    items = filterCategories(items);
  }

  if (items.length === 0) {
    return null;
  }

  let header = (
    <PanelHeader
      title={t('landing', 'personalized-products.title')}
      subtitle={t('landing', 'personalized-recommendations.subtitle')}
    >
      <InternalLink to="/personalization">
        {t('account', 'menu.settings')}
      </InternalLink>
    </PanelHeader>
  );

  if (products.fallbackToDefault) {
    header = <PanelHeader title={t('landing', 'hot-products.title')} />;
  }

  return (
    <Panel header={header} data-test-type="product-recommendations">
      <MatchMedia
        debounce={1000}
        serverSideRender={{ tablet: false }}
        matches={[
          {
            mediaQuery: '(min-width: 640px) and (max-width: 1000px)',
            values: { tablet: true },
          },
          {
            mediaQuery: 'all',
            values: { tablet: false },
          },
        ]}
      >
        {({ tablet }) => (
          <ExpandableGrid
            limit={tablet ? 3 : 4}
            expandText={t('common', 'button.show-more')}
            collapseText={t('common', 'button.show-less')}
            items={items.map((product, index) => (
              <ProductCard
                key={product.id}
                link={product.pathName}
                name={product.name}
                image={
                  <ProductImage name={product.name} src={product.media.first} />
                }
                price={
                  product.priceSummary.regular ||
                  product.priceSummary.alternative
                }
                rating={product.userReviewSummary.rating}
                onClick={() => {
                  props.productNavigation(productTagManagerAction, index + 1);
                }}
              />
            ))}
          />
        )}
      </MatchMedia>
    </Panel>
  );
};

ProductRecommendations.propTypes = {
  products: PropTypes.shape({
    fallbackToDefault: PropTypes.bool,
    nodes: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        pathName: PropTypes.string,
        media: PropTypes.shape({
          first: PropTypes.string,
        }),
        userReviewSummary: PropTypes.shape({
          rating: PropTypes.number,
          count: PropTypes.number,
        }),
        priceSummary: PropTypes.shape({
          regular: PropTypes.number,
          alternative: PropTypes.number,
          inStock: PropTypes.number,
        }),
        category: PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
          name: PropTypes.string,
        }),
      })
    ),
  }),
  productNavigation: PropTypes.func.isRequired,
};

ProductRecommendations.defaultProps = {
  products: {
    fallbackToDefault: true,
    nodes: [],
  },
};

export default connect(() => ({}), { productNavigation })(
  memo(ProductRecommendations)
);
